/**
 * Loop guard for 401-driven re-authentication.
 *
 * The trap: if the API rejects our token for a reason a fresh sign-in cannot fix — a
 * misconfigured audience, a user with no team, an API-side JWKS problem — then
 * "401 triggers re-auth" is an infinite redirect loop that never shows the user an
 * error. So each attempt is spent, and only a subsequent successful request refills it.
 *
 * **The state must outlive the document.** Re-auth is a full-page redirect, so an
 * in-memory flag is destroyed before it can ever be read back: the guard rebuilds itself
 * unspent on the next load and the loop it exists to prevent runs forever. That was a real
 * bug, and one no jsdom test could catch, because jsdom never actually navigates.
 *
 * sessionStorage rather than localStorage: the guard describes one tab's sign-in attempt,
 * and a stuck flag must not follow the user into a new tab where a fresh sign-in might
 * genuinely work.
 */
const KEY = 'orm.reauth.spent';

export class ReauthGuard {
  /** False when re-auth has already been tried and nothing has succeeded since. */
  mayAttempt(): boolean {
    return read() !== '1';
  }

  consume(): void {
    write('1');
  }

  /** A request the API accepted proves the session works, so re-arm. */
  reset(): void {
    clear();
  }
}

/*
 * Storage access is wrapped because it throws, not returns null, when a browser blocks
 * it — Safari private mode and some enterprise policies do. A guard that cannot read its
 * state must fail toward *showing an error* rather than toward redirecting, so an
 * unreadable store reads as "already spent".
 */
function read(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return '1';
  }
}

function write(value: string): void {
  try {
    sessionStorage.setItem(KEY, value);
  } catch {
    /* Nothing to do: read() already fails closed. */
  }
}

function clear(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* As above. */
  }
}
